import React, { useState, useEffect } from 'react';
import { Redirect, NavLink } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { setKey, session } from "../store/actions";
import { connect } from "react-redux";
import utils from '../utils/utils';
import DataGrid from './DataGrid';
import userService from '../services/user';
import { swalError } from "../utils/swal";
import datetime from '../utils/datetime';
import DateRangePicker from './DateRangePicker';

function Users({
    session,
    setKey,
    ...props
}) {
    const { t } = useTranslation();
    const [redirectTo, setRedirectTo] = useState(null);
    const [keyword, setKeyword] = useState(``);
    const [from, setFrom] = useState(datetime.startOfYear);
    const [to, setTo] = useState(datetime.endOfYear);
    const [rows, setRows] = useState([]);

    useEffect(() => {
        reload();
    }, [keyword, from]);

    const reload = async () => {
        await userService.getAll(session.token, keyword, from, to)
            .then(result => {
                if (result.error) {
                    swalError(result.error);
                    return;
                }

                if (Array.isArray(result.data)) {
                    setRows(result.data);
                } else {
                    setRows([]);
                    console.error("API returned invalid data (expected array):", result.data);
                }
            });
    }

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newUser, setNewUser] = useState({ name: '', email: '', password: '', accountType: 'personal' });

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setKey(session.isLoading, true);
        const result = await import('../services/admin').then(m => m.default.createUser(session.token, newUser));
        if (result.error) {
            swalError(result.error);
        } else {
            console.log("User created!");
            setNewUser({ name: '', email: '', password: '', accountType: 'personal' });
            setShowCreateModal(false);
            reload();
        }
        setKey(session.isLoading, false);
    };

    const _clms = [{
        headerName: t("col_name"),
        field: "name",
        pinned: "left"
    }, {
        headerName: t("col_email"),
        field: "email"
    }, {
        headerName: t("col_phone"),
        field: "phone"
    }, {
        headerName: t("col_active"),
        field: "active",
        cellRenderer: params => {
            return params.value === true
                ? `<span class="grid-badge-active">${t("col_active")}</span>`
                : `<span class="grid-badge-inactive">${t("col_inactive")}</span>`;
        }
    }, {
        headerName: t("col_type"),
        field: "accountType",
        cellRenderer: params => utils.capitalizeFirstLetter(params.value || 'personal')
    }, {
        headerName: t("col_date_joined"),
        field: "joined",
        cellRenderer: p => utils.dayMonthYearFormat(p.value)
    }, {
        headerName: t("col_actions"),
        field: "_id",
        minWidth: 120,
        cellRenderer: params => {
            const btn = document.createElement('button');
            btn.className = 'grid-action-btn';
            btn.innerText = t("btn_view");
            btn.addEventListener('click', () => handleView(params.node.data._id));
            return btn;
        }
    }];

    const handleView = id => {
        setRedirectTo(`/users/${id}`);
    }

    return (
        <div className="fade-in">
            {redirectTo && <Redirect push to={redirectTo} />}

            {showCreateModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{t("create_new_user")}</h5>
                                <button type="button" className="close" onClick={() => setShowCreateModal(false)}>
                                    <span>&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleCreateUser}>
                                    <div className="form-group">
                                        <label>{t("col_name")}</label>
                                        <input className="form-control" required value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>{t("col_email")}</label>
                                        <input type="email" className="form-control" required value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>{t("password_label")}</label>
                                        <input type="text" className="form-control" required value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>{t("account_type")}</label>
                                        <select className="form-control" value={newUser.accountType} onChange={e => setNewUser({ ...newUser, accountType: e.target.value })}>
                                            <option value="personal">{t("account_personal")}</option>
                                            <option value="business">{t("account_business")}</option>
                                            <option value="official">{t("account_official")}</option>
                                        </select>
                                    </div>
                                    <button type="submit" className="btn btn-primary btn-block">{t("btn_create_user")}</button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="page-header d-flex justify-content-between align-items-center">
                <div>
                    <h1 className="page-title">{t("users_management")}</h1>
                    <div className="text-muted-modern">{t("total_users")}: {rows.length}</div>
                </div>
                <button className="btn-modern btn-success-modern" onClick={() => setShowCreateModal(true)}>
                    <i className="fa fa-plus-circle mr-2"></i> {t("create_new_user")}
                </button>
            </div>

            <div className="card-modern">
                <div className="row mb-4 align-items-end">
                    <div className="col-md-4">
                        <label className="text-sm font-weight-bold ml-1 mb-2 text-muted-modern">{t("label_search")}</label>
                        <div className="input-group">
                            <input
                                type="text"
                                className="form-control-modern"
                                placeholder={t("search_users_placeholder")}
                                value={keyword}
                                onChange={e => setKeyword(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="col-md-4">
                        <label className="text-sm font-weight-bold ml-1 mb-2 text-muted-modern">{t("label_date_range")}</label>
                        <div style={{ display: 'block' }}>
                            <DateRangePicker onDatesChange={(from, to) => { setFrom(from); setTo(to); }} />
                        </div>
                    </div>
                    <div className="col-md-4 text-right">
                        <button className="btn-modern btn-primary-modern" onClick={() => reload()}>
                            <i className="fa fa-sync-alt"></i> {t("btn_refresh_data")}
                        </button>
                    </div>
                </div>

                <div style={{ height: '500px', width: '100%' }}>
                    <DataGrid
                        columnDefs={_clms}
                        rowData={rows}
                    />
                </div>
            </div>
        </div>
    );
}

const mapStateToProps = store => ({
    session: store.session
});

const mapDispatchToProps = dispatch => ({
    setKey: (key, value) => dispatch(setKey(key, value))
});

export default connect(mapStateToProps, mapDispatchToProps)(Users);